/* eslint-disable no-unused-vars */
import React, {useEffect, useMemo, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, KTCardHeader, Select, Loading} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {allCompanyLookup, projectLookup} from '../../../redux/slices/lookup.slice'
import {
  subcontractorDocumentCreate,
  setRedirect,
} from '../../../redux/slices/subcontractordocument.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  companyLookup: state.lookup.companyLookup,
  loading: state.lookup.loading,
})

const selectSubcontractorDocumentData = (state) => ({
  isRedirect: state.subcontractorDocument.isRedirect,
  loading: state.subcontractorDocument.loading,
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {projectLookupList, companyLookup, loading} = useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectSubcontractorDocumentData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(setRedirect(false))
    dispatch(allCompanyLookup())
    dispatch(projectLookup())
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        CompanyId: Yup.number()
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'}))
          .moreThan(0, intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        ProjectId: Yup.number()
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'}))
          .moreThan(0, intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        Year: Yup.number()
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'}))
          .min(2000)
          .max(2100),
        Month: Yup.number()
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'}))
          .min(1)
          .max(12),
      }),
    [intl]
  )

  const formValues = useMemoizedFormValues({
    CompanyId: '',
    ProjectId: '',
    Year: new Date().getFullYear(),
    Month: new Date().getMonth() + 1,
    Description: '',
  })

  const handleSubmit = useCallback(
    async (values, {setSubmitting}) => {
      setSubmitting(true)
      dispatch(subcontractorDocumentCreate(values))
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const handleRedirect = useCallback(() => {
    if (isRedirect && isRedirect.id) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
    }
  }, [isRedirect, navigate, pageUrl])

  useEffect(() => {
    handleRedirect()
  }, [handleRedirect])

  const months = useMemo(
    () =>
      Array.from({length: 12}, (_, i) => ({
        id: i + 1,
        name: intl.formatDate(new Date(2000, i, 1), {month: 'long'}),
      })),
    [intl]
  )

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT'}),
        path: '/projects/subcontractor-document',
        isSeparator: false,
        isActive: false,
      },
    ],
    [intl]
  )

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT.ADD'})}
      </PageTitle>
      <KTCard>
        <KTCardHeader title={intl.formatMessage({id: 'BC.SUBCONTRACTORDOCUMENT.ADD'})} />
        <KTCardBody>
          <form className='form' onSubmit={formik.handleSubmit} noValidate>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.CREATE.COMPANYID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={companyLookup}
                  required
                  {...formik.getFieldProps('CompanyId')}
                  onChange={(item) => formik.setFieldValue('CompanyId', Number(item && item.value))}
                />
                {formik.touched.CompanyId && formik.errors.CompanyId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.CompanyId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.CREATE.PROJECTID'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={projectLookupList}
                  required
                  {...formik.getFieldProps('ProjectId')}
                  onChange={(item) => formik.setFieldValue('ProjectId', Number(item && item.value))}
                />
                {formik.touched.ProjectId && formik.errors.ProjectId && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.ProjectId}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.CREATE.YEAR'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='number'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Year')}
                />
                {formik.touched.Year && formik.errors.Year && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Year}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.CREATE.MONTH'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Select
                  data={months}
                  required
                  {...formik.getFieldProps('Month')}
                  onChange={(item) => formik.setFieldValue('Month', Number(item && item.value))}
                />
                {formik.touched.Month && formik.errors.Month && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Month}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2'>
                {intl.formatMessage({id: 'SUBCONTRACTORDOCUMENT.CREATE.DESCRIPTION'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <textarea
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  rows={5}
                  disabled={formik.isSubmitting}
                  placeholder={intl.formatMessage({
                    id: 'SUBCONTRACTORDOCUMENT.CREATE.DESCRIPTION.PLACEHOLDER',
                  })}
                  {...formik.getFieldProps('Description')}
                />
              </div>
            </div>

            <div className='row pt-15'>
              <div className='col-lg-9 offset-lg-3 fv-row fv-plugins-icon-container d-flex gap-3'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {(loading | createLoading) > 0 && <Loading />}
    </>
  )
}
export {Create}
